import {
  AccountAttributeTile,
  AccountCopyAttributeTile,
  AccountProductEnum,
  AccountToggleAttributeTile,
  AddMoneyTile,
  ContentCtaTile,
  ITile,
  ListTile,
  ListTileType,
  QuickActionTile,
  TileType,
} from '@varo/graphql-schema-types';
import AccountAttributeTileComponent from 'features/accounts/AccountPageTiles/AccountAttributeTile';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import ProductOfferCard from 'shared/components/vxl/cards/ProductOfferCard/ProductOfferCard';
import theme from 'shared/styles/theme';

interface AccountPageTileProps {
  tiles: ITile[] | undefined;
  handleTilePress: (tile: ITile) => void;
  productType?: AccountProductEnum;
}

const PAY_CARD = 'Pay card';

const AccountPageTiles = ({
  tiles,
  handleTilePress,
  productType,
}: AccountPageTileProps) => {
  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'accountPageTiles',
  });
  return (
    <AccountTiles
      tiles={tiles}
      handleTilePress={handleTilePress}
      translatedText={translatedText}
      productType={productType}
    />
  );
};

interface AccountTileProps {
  tiles: ITile[] | undefined;
  handleTilePress: (tile: ITile) => void;
  translatedText: (key: string) => string;
  isHorizontalLayout?: boolean;
  productType?: AccountProductEnum;
}
const getAnalyticsAndAttributes = (tile: QuickActionTile) => {
  if (tile.title === PAY_CARD) {
    return {
      analyticsId: 'payCardClick',
      customAttributes: { cta: PAY_CARD, account_type: 'charge_card' },
    };
  }
  return {
    analyticsId: '',
    customAttributes: null,
  };
};

const AccountTiles = ({
  tiles,
  handleTilePress,
  translatedText,
  isHorizontalLayout,
  productType,
}: AccountTileProps) => (
  <View
    style={
      (isHorizontalLayout ?? true)
        ? styles.container
        : styles.horizontalContainer
    }
  >
    {tiles?.map((tile: ITile, index: number) => {
      switch (tile.tileType) {
        case TileType.AddMoney:
          return (
            <View key={tile.id} style={styles.buttonWrapper}>
              <Button
                key={tile.id}
                label={(tile as AddMoneyTile)?.cta}
                onPress={() => handleTilePress(tile)}
              />
            </View>
          );
        case TileType.MoveMoney:
          return (
            <View key={tile.id} style={styles.buttonWrapper}>
              <Button
                key={tile.id}
                label={(tile as AddMoneyTile)?.cta}
                onPress={() => handleTilePress(tile)}
              />
            </View>
          );
        case TileType.List:
          return (
            <View key={tile.id}>
              <AccountTiles
                tiles={(tile as ListTile).tiles}
                handleTilePress={handleTilePress}
                translatedText={translatedText}
                isHorizontalLayout={
                  (tile as ListTile).listTileType !== ListTileType.QuickActions
                }
                productType={productType}
              />
            </View>
          );
        case TileType.DetailsLink:
          // This tile indicates that we need to show settings icon on the top right corner
          return <View key={tile.id} />;
        case TileType.EmptyStateContent:
          return <View key={tile.id} />;
        case TileType.QuickAction:
          // design is getting ride of the quick actions in cruise
          return (
            <View key={tile.id} style={styles.flexAuto}>
              <Button
                label={(tile as QuickActionTile).title}
                onPress={() => {
                  handleTilePress(tile);
                }}
                {...getAnalyticsAndAttributes(tile as QuickActionTile)}
              />
            </View>
          );
        case TileType.AccountAttribute:
          return (
            <View key={tile.id}>
              <AccountAttributeTileComponent
                tile={
                  tile as
                    | AccountCopyAttributeTile
                    | AccountToggleAttributeTile
                    | AccountAttributeTile
                }
                handleTilePress={handleTilePress}
                isLastTile={index === tiles.length - 1}
                productType={productType}
              />
            </View>
          );
        case TileType.ContentCta:
          return (
            <View key={tile.id}>
              <View style={styles.divider} />
              <View style={styles.contentCTAWrapper}>
                <ProductOfferCard
                  key={(tile as ContentCtaTile).id}
                  titleText={(tile as ContentCtaTile).title}
                  bodyText={(tile as ContentCtaTile).description}
                  buttonText={translatedText('seeProgress')}
                  illustrationKey="PIGGY_BANK_A"
                  illustrationBackgroundColor={theme.color.expressive.orchid}
                  onPress={() => handleTilePress(tile)}
                  hasBorder
                />
              </View>
            </View>
          );
        default:
          return null;
      }
    })}
  </View>
);

const styles = StyleSheet.create({
  buttonWrapper: {
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  container: {
    backgroundColor: theme.color.background.surface,
    flex: 1,
  },
  contentCTAWrapper: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
  },
  divider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  flexAuto: {
    flex: 1,
    paddingTop: theme.spacing.s,
  },
  horizontalContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'row',
    gap: theme.spacing.m,
    justifyContent: 'space-between',
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
});

export default AccountPageTiles;
